import React from "react";
import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import filePenIcon from "@/assets/svg/file-pen.svg";

interface NotesPopoverCellProps {
  notes?: string[];
  count?: number;
  title: string;
  emptyMessage: string;
  ariaLabel: string;
  className?: string;
  to?: string;
}

const getVisibleNotes = (notes: string[] = []) =>
  notes.filter((note) => note.trim().length > 0);

export const NotesPopoverCell: React.FC<NotesPopoverCellProps> = ({
  notes = [],
  count,
  title,
  emptyMessage,
  ariaLabel,
  className,
  to,
}) => {
  const visibleNotes = getVisibleNotes(notes);
  const noteCount = count ?? visibleNotes.length;
  const hasNotes = noteCount > 0;

  if (to) {
    return (
      <div className="flex justify-center">
        <Link
          to={to}
          onClick={(e) => e.stopPropagation()}
          aria-label={ariaLabel}
          className={cn(
            "relative flex size-8 items-center justify-center rounded-lg bg-light-indigo text-indigo transition-colors hover:bg-light-indigo/80",
            !hasNotes && "opacity-50",
            className,
          )}
        >
          <img src={filePenIcon} alt="" aria-hidden="true" className="size-4" />
          {hasNotes && (
            <Badge
              variant="number_default"
              className="absolute -right-1.5 -top-1.5 h-5 min-w-5 rounded-full px-1.5 text-xs leading-none"
            >
              {noteCount}
            </Badge>
          )}
        </Link>
      </div>
    );
  }

  const trigger = (
    <Button
      type="button"
      variant="span"
      size="icon_sm"
      aria-label={ariaLabel}
      aria-disabled={!hasNotes}
      tabIndex={hasNotes ? undefined : -1}
      onClick={(event) => event.stopPropagation()}
      className={cn(
        "relative mx-auto size-8 rounded-lg bg-light-indigo text-indigo transition-colors hover:bg-light-indigo/80 focus-visible:ring-custom-outline/50",
        !hasNotes && "cursor-default opacity-50 hover:bg-light-indigo",
        className,
      )}
    >
      <img src={filePenIcon} alt="" aria-hidden="true" className="size-4" />
      {hasNotes && (
        <Badge
          variant="number_default"
          className="absolute -right-1.5 -top-1.5 h-5 min-w-5 rounded-full px-1.5 text-xs leading-none"
        >
          {noteCount}
        </Badge>
      )}
    </Button>
  );

  if (!hasNotes) {
    return <div className="flex justify-center">{trigger}</div>;
  }

  return (
    <div className="flex justify-center">
      <Popover>
        <PopoverTrigger asChild>{trigger}</PopoverTrigger>
        <PopoverContent
          align="end"
          side="top"
          sideOffset={8}
          className="w-72 overflow-hidden p-0"
          onClick={(event) => event.stopPropagation()}
        >
          <div className="border-b border-base-border px-3 py-2">
            <div className="flex items-center justify-between gap-3">
              <div className="flex min-w-0 items-center gap-2">
                <div className="flex size-7 shrink-0 items-center justify-center rounded-lg bg-light-indigo text-indigo">
                  <img
                    src={filePenIcon}
                    alt=""
                    aria-hidden="true"
                    className="size-4"
                  />
                </div>
                <p className="truncate text-sm font-semibold text-foreground">
                  {title}
                </p>
              </div>
              <Badge variant="number_default" className="shrink-0">
                {noteCount}
              </Badge>
            </div>
          </div>
          <div className="max-h-72 overflow-y-auto p-2">
            {visibleNotes.length > 0 ? (
              <ul className="flex flex-col gap-2">
                {visibleNotes.map((note, index) => (
                  <li
                    key={`${note}-${index}`}
                    className="rounded-md border border-base-border bg-background px-3 py-2 text-left text-sm leading-5 text-foreground shadow-xs"
                  >
                    <p className="whitespace-pre-wrap break-words">{note}</p>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="px-3 py-4 text-center text-sm text-muted-foreground">
                {emptyMessage}
              </p>
            )}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
};
